import { createContext, forwardRef, useContext, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn.js';

/**
 * Form primitives.
 *
 * The `Field` wrapper owns the relationship between label, description, error
 * and control: it generates the ids and wires `aria-describedby` /
 * `aria-invalid` so a control cannot end up unlabelled or with an error that
 * screen readers never hear.
 */

interface FieldContextValue {
  inputId: string;
  descriptionId: string | undefined;
  errorId: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: string;
  /** Hides the visual label but keeps it available to assistive technology. */
  labelHidden?: boolean;
  description?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Rendered to the right of the label, e.g. an "optional" hint or a link. */
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  labelHidden = false,
  description,
  error,
  required = false,
  aside,
  className,
  children,
}: FieldProps) {
  const base = useId();
  const inputId = `${base}-input`;
  const descriptionId = description ? `${base}-description` : undefined;
  const errorId = error ? `${base}-error` : undefined;

  return (
    <FieldContext.Provider
      value={{ inputId, descriptionId, errorId, invalid: Boolean(error), required }}
    >
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div className="flex items-baseline justify-between gap-3">
          <label
            htmlFor={inputId}
            className={cn(
              'text-sm font-medium text-warm-white',
              labelHidden && 'sr-only',
            )}
          >
            {label}
            {required && (
              <>
                <span aria-hidden="true" className="ml-1 text-accent-boxing">
                  *
                </span>
                <span className="sr-only"> (required)</span>
              </>
            )}
          </label>
          {aside && <span className="text-xs text-cool-steel">{aside}</span>}
        </div>

        {description && (
          <p id={descriptionId} className="text-xs leading-relaxed text-cool-steel">
            {description}
          </p>
        )}

        {children}

        {error && (
          <p id={errorId} className="flex items-start gap-1.5 text-xs font-medium text-accent-boxing">
            <span aria-hidden="true">!</span>
            <span>{error}</span>
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

const CONTROL_BASE =
  'w-full rounded-md border bg-near-black/60 px-3 text-warm-white placeholder:text-cool-steel/70 ' +
  'transition-colors duration-150 focus:outline-hidden focus:ring-2 focus:ring-offset-1 focus:ring-offset-near-black ' +
  'disabled:cursor-not-allowed disabled:opacity-50 read-only:opacity-80';

const CONTROL_VALID = 'border-white/15 focus:border-accent-pt focus:ring-accent-pt/60';
const CONTROL_INVALID = 'border-accent-boxing/70 focus:border-accent-boxing focus:ring-accent-boxing/60';

function controlAria(field: FieldContextValue | null) {
  if (!field) return {};
  const describedBy = [field.descriptionId, field.errorId].filter(Boolean).join(' ');
  return {
    id: field.inputId,
    'aria-invalid': field.invalid || undefined,
    'aria-describedby': describedBy.length > 0 ? describedBy : undefined,
    required: field.required || undefined,
  };
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    const field = useFieldContext();
    return (
      <input
        ref={ref}
        className={cn(
          CONTROL_BASE,
          'min-h-11',
          field?.invalid ? CONTROL_INVALID : CONTROL_VALID,
          className,
        )}
        {...controlAria(field)}
        {...rest}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 4, ...rest }, ref) {
    const field = useFieldContext();
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          CONTROL_BASE,
          'py-2.5 leading-relaxed',
          field?.invalid ? CONTROL_INVALID : CONTROL_VALID,
          className,
        )}
        {...controlAria(field)}
        {...rest}
      />
    );
  },
);

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: readonly SelectOption[];
  /** Rendered as a disabled first option, e.g. "Choose a session type". */
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, ...rest },
  ref,
) {
  const field = useFieldContext();
  return (
    <select
      ref={ref}
      className={cn(
        CONTROL_BASE,
        'min-h-11 appearance-none bg-[length:12px] bg-[right_0.75rem_center] bg-no-repeat pr-9',
        field?.invalid ? CONTROL_INVALID : CONTROL_VALID,
        className,
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none'><path d='M1 1.5 6 6.5l5-5' stroke='%238D97A4' stroke-width='1.6' stroke-linecap='round'/></svg>\")",
      }}
      {...controlAria(field)}
      {...rest}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
  error?: string | null;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, error, className, ...rest },
  ref,
) {
  const base = useId();
  const descriptionId = description ? `${base}-description` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ');

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-start gap-3">
        <input
          ref={ref}
          id={base}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy.length > 0 ? describedBy : undefined}
          className={cn(
            'mt-0.5 size-5 shrink-0 cursor-pointer rounded border-white/25 bg-near-black text-accent-pt',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-pt focus-visible:ring-offset-2 focus-visible:ring-offset-near-black',
            error && 'border-accent-boxing',
          )}
          {...rest}
        />
        <label htmlFor={base} className="cursor-pointer text-sm leading-relaxed text-warm-white">
          {label}
        </label>
      </div>
      {description && (
        <p id={descriptionId} className="pl-8 text-xs leading-relaxed text-cool-steel">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="pl-8 text-xs font-medium text-accent-boxing">
          {error}
        </p>
      )}
    </div>
  );
});

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  legend: string;
  legendHidden?: boolean;
  options: readonly RadioOption[];
  value: string | null;
  onChange: (value: string) => void;
  error?: string | null;
  description?: ReactNode;
  required?: boolean;
  /** Card layout gives a larger touch target and room for descriptions. */
  layout?: 'list' | 'cards';
  className?: string;
}

export function RadioGroup({
  name,
  legend,
  legendHidden = false,
  options,
  value,
  onChange,
  error,
  description,
  required = false,
  layout = 'list',
  className,
}: RadioGroupProps) {
  const base = useId();
  const descriptionId = description ? `${base}-description` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ');

  return (
    <fieldset className={cn('flex flex-col gap-2', className)}>
      <legend className={cn('text-sm font-medium text-warm-white', legendHidden && 'sr-only')}>
        {legend}
        {required && (
          <>
            <span aria-hidden="true" className="ml-1 text-accent-boxing">
              *
            </span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </legend>

      {description && (
        <p id={descriptionId} className="text-xs leading-relaxed text-cool-steel">
          {description}
        </p>
      )}

      {/*
        Native fieldset maps to role=group, which rejects aria-required,
        aria-invalid and aria-describedby under axe. The required state lives
        on the radio inputs (where HTML and ARIA both allow it); the error is
        announced via role=alert below rather than aria-invalid on the group.
      */}
      <div className={cn(layout === 'cards' ? 'grid gap-2 sm:grid-cols-2' : 'flex flex-col gap-2')}>
        {options.map((option) => {
          const id = `${base}-${option.value}`;
          const checked = value === option.value;
          return (
            <label
              key={option.value}
              htmlFor={id}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent-pt has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-near-black',
                checked
                  ? 'border-accent-pt/70 bg-accent-pt/10'
                  : 'border-white/12 bg-white/[0.02] hover:border-white/30',
                option.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                required={required}
                aria-describedby={describedBy.length > 0 ? describedBy : undefined}
                disabled={option.disabled}
                onChange={() => onChange(option.value)}
                className="mt-0.5 size-4 shrink-0 border-white/25 bg-near-black text-accent-pt focus:outline-hidden"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-warm-white">{option.label}</span>
                {option.description && (
                  <span className="text-xs leading-relaxed text-cool-steel">
                    {option.description}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {error && (
        <p id={errorId} className="text-xs font-medium text-accent-boxing" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  className,
}: SwitchProps) {
  const base = useId();
  const descriptionId = description ? `${base}-description` : undefined;

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <span className="flex flex-col gap-0.5">
        <span id={base} className="text-sm font-medium text-warm-white">
          {label}
        </span>
        {description && (
          <span id={descriptionId} className="text-xs leading-relaxed text-cool-steel">
            {description}
          </span>
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={base}
        aria-describedby={descriptionId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-pt focus-visible:ring-offset-2 focus-visible:ring-offset-near-black',
          checked ? 'bg-accent-pt' : 'bg-graphite',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none inline-block size-5 transform rounded-full bg-warm-white shadow-sm transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  );
}

/**
 * Error summary shown at the top of a submitted form.
 *
 * WCAG 3.3.1/3.3.3 pattern: on failed submission focus moves here, and each
 * entry links to the offending control.
 */
export function ErrorSummary({
  errors,
  title = 'There is a problem',
  className,
}: {
  errors: { field: string; message: string }[];
  title?: string;
  className?: string;
}) {
  if (errors.length === 0) return null;

  return (
    <div
      role="alert"
      tabIndex={-1}
      data-testid="error-summary"
      className={cn(
        'rounded-md border border-accent-boxing/50 bg-accent-boxing/10 p-4 outline-hidden',
        className,
      )}
    >
      <h2 className="text-sm font-semibold text-warm-white">{title}</h2>
      <ul className="mt-2 flex flex-col gap-1">
        {errors.map((error) => (
          <li key={error.field}>
            <a
              href={`#${error.field}`}
              className="text-sm text-accent-boxing underline decoration-accent-boxing/50 underline-offset-2 hover:decoration-accent-boxing"
            >
              {error.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Groups related controls with a visible heading. */
export function Fieldset({
  legend,
  description,
  children,
  className,
}: {
  legend: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-col gap-1">
        <legend className="text-base font-semibold text-warm-white">{legend}</legend>
        {description && (
          <p className="text-sm leading-relaxed text-cool-steel">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}
